import { useState, useEffect } from 'react';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import { ThreeDots } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { fNumber } from '../../../utils/formatNumber';
import { Page } from '../../../components';
import Label from '../../../components/label';
import { getAuditTrail, exportAuditTrail } from '../store/auditTrailActions';

const TABLE_HEAD = ['Timestamp', 'Actor', 'Action', 'Resource Type', 'Resource Name', 'Changes', 'IP Address'];

const ACTION_OPTIONS = [
  { label: 'All Actions', value: '' },
  { label: 'Created', value: '0' },
  { label: 'Updated', value: '1' },
  { label: 'Deleted', value: '2' },
];

const ACTION_COLORS = {
  Created: 'success',
  Updated: 'warning',
  Deleted: 'error',
  Accessed: 'info',
};

function formatChanges(changes) {
  if (!changes || typeof changes !== 'object') return '—';
  const entries = Object.entries(changes);
  if (entries.length === 0) return '—';
  return (
    entries
      .slice(0, 2)
      .map(([field, vals]) => {
        if (Array.isArray(vals) && vals.length === 2) {
          return `${field}: ${vals[0] ?? 'null'} → ${vals[1] ?? 'null'}`;
        }
        return field;
      })
      .join(', ') + (entries.length > 2 ? ` +${entries.length - 2} more` : '')
  );
}

function ChangesCell({ changes, resourceName }) {
  const [open, setOpen] = useState(false);
  const hasChanges = changes && typeof changes === 'object' && Object.keys(changes).length > 0;

  if (!hasChanges) return <Typography variant="caption" color="text.secondary">—</Typography>;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {formatChanges(changes)}
        </Typography>
        <Tooltip title="View full changes">
          <IconButton size="small" onClick={() => setOpen(true)}>
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Changes — {resourceName}
        </DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Before</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>After</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(changes).map(([field, vals]) => (
                <TableRow key={field}>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{field.replace(/_/g, ' ')}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="error.main">
                      {Array.isArray(vals) ? (vals[0] ?? 'null') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="success.main">
                      {Array.isArray(vals) ? (vals[1] ?? 'null') : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AuditTrailView() {
  const dispatch = useDispatch();
  const { isLoading, trailRecords, count } = useSelector((state) => state.auditTrailReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState({
    model: '',
    action: '',
    startDate: '',
    endDate: '',
  });

  const fetchData = (pg = page, rpp = rowsPerPage, f = filters) => {
    dispatch(getAuditTrail({ page: pg + 1, rowsPerPage: rpp, ...f }));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchData(0, rowsPerPage, filters);
  };

  const handleResetFilters = () => {
    const reset = { model: '', action: '', startDate: '', endDate: '' };
    setFilters(reset);
    setPage(0);
    fetchData(0, rowsPerPage, reset);
  };

  const handleExport = async () => {
    setIsExporting(true);
    await dispatch(exportAuditTrail(filters));
    setIsExporting(false);
  };

  return (
    <Page title="Audit Trail">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HistoryIcon color="primary" />
            <Typography variant="h5">Audit Trail</Typography>
          </Stack>
          <Button
            variant="contained"
            color="success"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </Stack>

        {/* Stats card */}
        <Box mb={3}>
          <Paper elevation={3} sx={{ p: 2, display: 'inline-block', minWidth: 160 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Records
            </Typography>
            <Typography variant="h4">{fNumber(count)}</Typography>
          </Paper>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Filters" />
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
              <TextField
                label="Resource Type"
                placeholder="e.g. asset, user"
                size="small"
                value={filters.model}
                onChange={(e) => handleFilterChange('model', e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  label="Action"
                  value={filters.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                >
                  {ACTION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="From Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="To Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" onClick={handleApplyFilters}>
                  Apply
                </Button>
                <Button variant="outlined" onClick={handleResetFilters}>
                  Reset
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <ThreeDots color="#1976d2" height={50} width={50} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {TABLE_HEAD.map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 'bold' }}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trailRecords && trailRecords.length > 0 ? (
                      trailRecords.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell>
                            {row.actor ? (
                              <Stack>
                                <Typography variant="body2" fontWeight={500}>
                                  {row.actor.display_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.actor.email}
                                </Typography>
                              </Stack>
                            ) : (
                              <Chip label="System" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Label color={ACTION_COLORS[row.action] || 'default'}>{row.action}</Label>
                          </TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{row.resource_type}</TableCell>
                          <TableCell>{row.resource_name}</TableCell>
                          <TableCell>
                            <ChangesCell changes={row.changes} resourceName={row.resource_name} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{row.remote_addr || '—'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No audit trail records found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
